import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Trash, Plus, Info } from '@phosphor-icons/react'
import ReactTooltip from 'react-tooltip'
import { useTranslation } from 'react-i18next'

import RadioField from '../FormLegacy/FormFieldsV2/RadioField'

import Select from './Select'
import { prepareOptions } from './helper'
import EmergencyNotifications from './EmergencyNitifications'
import EmergencyPhoneField from './EmergencyPhoneField'

const EmergencyOrder = ({
  emergencyOrder,
  addLevel,
  removeLevel,
  updateLevel,
  staffPeople,
  propertyManagers,
  shiftManagers,
}) => {
  const { t } = useTranslation(['on-call-schedule'])
  const selectedUsers = useMemo(
    () =>
      emergencyOrder
        .map((item, index) => (index > 1 ? item.user.value : null))
        .filter((item) => item),
    [emergencyOrder],
  )

  const options = useMemo(
    () => [
      {
        label: t('on-call-schedule:groupLabels.maintenance'),
        options: prepareOptions(staffPeople, selectedUsers),
      },
      {
        label: t('on-call-schedule:groupLabels.propertyManager'),
        options: prepareOptions(propertyManagers, selectedUsers),
      },
      {
        label: t('on-call-schedule:groupLabels.frontDesk'),
        options: prepareOptions(shiftManagers, selectedUsers),
      },
    ],
    [propertyManagers, shiftManagers, staffPeople, selectedUsers, t],
  )

  return (
    <div className="on-call-schedule__escalations-tab">
      <ul className="escalations-order">
        {emergencyOrder.map((item, index) => {
          const { time, label, deletable, user, call, sms, email, push, contactType, phoneNumber } =
            item
          const isLastItem = label === 'last'

          return (
            <li className="escalations-order__item" key={time}>
              <p className="escalations-order__item--time">{time ? `${time} min` : ''}</p>
              <div
                className={`escalations-order__item--timeline ${isLastItem ? 'hide-border' : ''}`}
              >
                <div className="escalations-order__item--dot" />
                {time && label ? (
                  <div className="escalations-order__item--content">
                    {label && (
                      <h4 className="escalations-order__item--title">
                        {t(`on-call-schedule:emergencyOrder.labels.${label}`)}
                      </h4>
                    )}
                    {isLastItem && (
                      <div className="escalations-order__item--radio-wrap">
                        <RadioField
                          name="internal"
                          value="internal"
                          checked={contactType === 'internal'}
                          label={t('on-call-schedule:emergencyOrder.internalStaff')}
                          onChange={() =>
                            updateLevel(index, {
                              contactType: 'internal',
                              phoneNumber: '',
                            })
                          }
                        />
                        <RadioField
                          name="external"
                          value="external"
                          checked={contactType === 'external'}
                          label={t('on-call-schedule:emergencyOrder.externalPhone')}
                          onChange={() =>
                            updateLevel(index, {
                              contactType: 'external',
                              user: {
                                avatar: '',
                                value: '',
                                label: '',
                              },
                            })
                          }
                        />
                      </div>
                    )}
                    <div className="escalations-order__item--select-wrap">
                      {contactType === 'external' && isLastItem ? (
                        <EmergencyPhoneField
                          phoneNumber={phoneNumber}
                          handleChange={(phoneNumber) => updateLevel(index, { phoneNumber })}
                        />
                      ) : (
                        <Select
                          options={options}
                          setFieldValue={(value) => {
                            if (index !== 1) {
                              updateLevel(index, {
                                user: value,
                              })
                            }
                          }}
                          value={user && user.value ? user : null}
                          name="user"
                          placeholder={t('on-call-schedule:emergencyOrder.selectUser')}
                          disabled={index === 1}
                        />
                      )}
                      {index === 1 && (
                        <React.Fragment>
                          <div className="heading_text__tip" data-tip data-for="on-call-tip">
                            <Info size={20} />
                            <ReactTooltip id="on-call-tip" place="top" effect="solid">
                              {t('on-call-schedule:emergencyOrder.firstPersonTooltip')}
                            </ReactTooltip>
                          </div>
                          <button className="escalations-order__item--btn" onClick={addLevel}>
                            <Plus size={20} />
                          </button>
                        </React.Fragment>
                      )}
                      {deletable && (
                        <button
                          className="escalations-order__item--btn"
                          onClick={() => removeLevel(index)}
                        >
                          <Trash size={20} />
                        </button>
                      )}
                    </div>
                    <EmergencyNotifications
                      index={index}
                      call={call}
                      sms={sms}
                      email={email}
                      push={push}
                      updateLevel={updateLevel}
                    />
                  </div>
                ) : (
                  <h4 className="escalations-order__item--main-titile">
                    {t('on-call-schedule:emergencyOrder.emptyState')}
                  </h4>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

EmergencyOrder.propTypes = {
  emergencyOrder: PropTypes.array.isRequired,
  addLevel: PropTypes.func,
  removeLevel: PropTypes.func,
  updateLevel: PropTypes.func,
  shiftManagers: PropTypes.array,
  propertyManagers: PropTypes.array,
  staffPeople: PropTypes.array,
}

export default EmergencyOrder
